import { useRouteOrganizationId } from '@/hooks/useRouteOrganizationId'
import {
  buildAdminQueryRoot,
  buildScopedAdminQueryKey,
  type AdminQueryKey,
  type AdminQueryRoot,
  type OrganizationIdInput,
} from '@/lib/organization-scoping'
import { queryOptions, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  createAppUpdate,
  deleteAppUpdate,
  getAppUpdate,
  getAppUpdateCounts,
  getAppUpdates,
  markAppUpdateRead,
  markAppUpdatesViewed,
  markAppUpdateUnread,
  updateAppUpdate,
  type AppUpdateCountsKeyEntries,
  type AppUpdateIndexKeyEntries,
  type AppUpdateOrganizationScope,
  type AppUpdateShowKeyEntries,
  type AppUpdateTarget,
} from './app-updates.api'
import { APP_UPDATE_PAGE_SIZE } from './app-updates.schema'
import type {
  AppUpdateIdInput,
  AppUpdateIndexFilters,
  AppUpdateReadState,
  CreateAppUpdateInput,
  UpdateAppUpdateInput,
} from './app-updates.types'

/** Hooks default to the current route's organization. */
interface OrganizationScopeInput {
  organizationId?: OrganizationIdInput
}

interface AppUpdateIndexPage {
  filters?: AppUpdateIndexFilters | undefined
  offset?: number | undefined
  page?: number | undefined
  pageSize?: number | undefined
}

type AppUpdateIndexScope = AppUpdateOrganizationScope & AppUpdateIndexPage

/** The key entries each fetcher reads; the scoped key builders below set them. */
type AppUpdateCountsQueryKey = [AdminQueryRoot & AppUpdateCountsKeyEntries]
type AppUpdateIndexQueryKey = [AdminQueryRoot & AppUpdateIndexKeyEntries]
type AppUpdateShowQueryKey = [AdminQueryRoot & AppUpdateShowKeyEntries]

/** Query options a caller may override; the key and fetcher stay owned by this module. */
type QueryParams<TOptions> = Partial<Omit<TOptions, 'queryKey' | 'queryFn'>>

interface UpdateAppUpdateVariables {
  appUpdateId: AppUpdateIdInput
  values: UpdateAppUpdateInput
}

export const appUpdatesQueryKeys = {
  /** Without an organization, matches every organization's app update queries. */
  all: ({ organizationId }: OrganizationScopeInput = {}): AdminQueryKey => [
    buildAdminQueryRoot({ entity: 'app-updates' }, { organizationId }),
  ],
  counts: ({ organizationId }: AppUpdateOrganizationScope): AppUpdateCountsQueryKey =>
    buildScopedAdminQueryKey(appUpdatesQueryKeys.all({ organizationId })[0], {
      organizationId,
      scope: 'counts',
    }),
  index: ({
    organizationId,
    filters,
    offset = 0,
    page = 1,
    pageSize = APP_UPDATE_PAGE_SIZE,
  }: AppUpdateIndexScope): AppUpdateIndexQueryKey =>
    buildScopedAdminQueryKey(appUpdatesQueryKeys.all({ organizationId })[0], {
      organizationId,
      filters,
      offset,
      page,
      pageSize,
      scope: 'index',
    }),
  show: ({ organizationId, appUpdateId }: AppUpdateTarget): AppUpdateShowQueryKey =>
    buildScopedAdminQueryKey(appUpdatesQueryKeys.all({ organizationId })[0], {
      organizationId,
      appUpdateId: Number(appUpdateId),
      scope: 'show',
    }),
}

export const appUpdatesQueries = {
  counts: ({ organizationId }: AppUpdateOrganizationScope) =>
    queryOptions({
      queryKey: appUpdatesQueryKeys.counts({ organizationId }),
      queryFn: getAppUpdateCounts,
    }),
  index: ({
    organizationId,
    filters,
    offset = 0,
    page = 1,
    pageSize = APP_UPDATE_PAGE_SIZE,
  }: AppUpdateIndexScope) =>
    queryOptions({
      queryKey: appUpdatesQueryKeys.index({ organizationId, filters, offset, page, pageSize }),
      queryFn: getAppUpdates,
    }),
  show: ({ organizationId, appUpdateId }: AppUpdateTarget) =>
    queryOptions({
      queryKey: appUpdatesQueryKeys.show({ organizationId, appUpdateId }),
      queryFn: getAppUpdate,
    }),
}

export function useAppUpdateCounts({
  organizationId,
  ...params
}: OrganizationScopeInput & QueryParams<ReturnType<typeof appUpdatesQueries.counts>> = {}) {
  const resolvedOrganizationId = useRouteOrganizationId(organizationId)

  return useQuery({
    ...appUpdatesQueries.counts({ organizationId: resolvedOrganizationId }),
    ...params,
  })
}

export function useAppUpdates({
  organizationId,
  filters,
  offset = 0,
  page = 1,
  pageSize = APP_UPDATE_PAGE_SIZE,
  ...params
}: OrganizationScopeInput &
  AppUpdateIndexPage &
  QueryParams<ReturnType<typeof appUpdatesQueries.index>> = {}) {
  const resolvedOrganizationId = useRouteOrganizationId(organizationId)

  return useQuery({
    ...appUpdatesQueries.index({
      organizationId: resolvedOrganizationId,
      filters,
      offset,
      page,
      pageSize,
    }),
    ...params,
  })
}

export function useAppUpdate({
  organizationId,
  appUpdateId,
  ...params
}: OrganizationScopeInput & {
  appUpdateId: AppUpdateIdInput
} & QueryParams<ReturnType<typeof appUpdatesQueries.show>>) {
  const resolvedOrganizationId = useRouteOrganizationId(organizationId)

  return useQuery({
    ...appUpdatesQueries.show({
      organizationId: resolvedOrganizationId,
      appUpdateId,
    }),
    ...params,
  })
}

export function useMarkAppUpdatesViewedMutation() {
  const organizationId = useRouteOrganizationId()
  const queryClient = useQueryClient()
  const countsQueryKey = appUpdatesQueries.counts({ organizationId }).queryKey

  return useMutation({
    mutationFn: () => markAppUpdatesViewed({ organizationId }),
    onMutate: async () => {
      await queryClient.cancelQueries({ queryKey: countsQueryKey })

      const previousCounts = queryClient.getQueryData(countsQueryKey)

      queryClient.setQueryData(countsQueryKey, (currentCounts) => {
        if (!currentCounts) {
          return currentCounts
        }

        return {
          ...currentCounts,
          new_count: 0,
        }
      })

      return { previousCounts }
    },
    onError: (_error, _variables, context) => {
      if (context?.previousCounts) {
        queryClient.setQueryData(countsQueryKey, context.previousCounts)
      }
    },
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: appUpdatesQueryKeys.all(),
      }),
  })
}

function useAppUpdateReadStateMutation(
  mutationFn: (target: AppUpdateTarget) => Promise<AppUpdateReadState>
) {
  const organizationId = useRouteOrganizationId()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (appUpdateId: AppUpdateIdInput) => mutationFn({ organizationId, appUpdateId }),
    onSuccess: (_response, appUpdateId) => {
      void queryClient.invalidateQueries({
        queryKey: appUpdatesQueryKeys.all({ organizationId }),
      })
      void queryClient.invalidateQueries({
        queryKey: appUpdatesQueryKeys.show({ organizationId, appUpdateId }),
      })
    },
  })
}

export function useMarkAppUpdateReadMutation() {
  return useAppUpdateReadStateMutation(markAppUpdateRead)
}

export function useMarkAppUpdateUnreadMutation() {
  return useAppUpdateReadStateMutation(markAppUpdateUnread)
}

export function useCreateAppUpdateMutation() {
  const organizationId = useRouteOrganizationId()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (values: CreateAppUpdateInput) => createAppUpdate({ organizationId, values }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: appUpdatesQueryKeys.all({ organizationId }) })
    },
  })
}

export function useUpdateAppUpdateMutation() {
  const organizationId = useRouteOrganizationId()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ appUpdateId, values }: UpdateAppUpdateVariables) =>
      updateAppUpdate({ organizationId, appUpdateId, values }),
    onSuccess: (_response, variables) => {
      void queryClient.invalidateQueries({ queryKey: appUpdatesQueryKeys.all({ organizationId }) })
      void queryClient.invalidateQueries({
        queryKey: appUpdatesQueryKeys.show({
          organizationId,
          appUpdateId: variables.appUpdateId,
        }),
      })
    },
  })
}

export function useDeleteAppUpdateMutation() {
  const organizationId = useRouteOrganizationId()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (appUpdateId: AppUpdateIdInput) => deleteAppUpdate({ organizationId, appUpdateId }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: appUpdatesQueryKeys.all({ organizationId }) })
    },
  })
}
